
import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { Button } from '@/components/ui/Button';
import { CollabProposal, UserRole, ProposalStatus } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Download, CheckCircle, XCircle, MessageSquare, DollarSign, Trash2 } from 'lucide-react';
import { DataTablePagination } from './DataTablePagination';
import { Badge } from '@/components/ui/Badge';
import mockProposalData from '../../../public/proposals.json';

const getStatusBadge = (status: ProposalStatus) => {
    switch (status) {
      case ProposalStatus.PENDING:
        return <Badge variant="warning">Pending Admin Approval</Badge>;
      case ProposalStatus.ACTIVE:
        return <Badge variant="secondary">Active & Pending Recipient</Badge>;
      case ProposalStatus.ACCEPTED:
        return <Badge variant="success">Accepted</Badge>;
      case ProposalStatus.REJECTED:
        return <Badge variant="destructive">Rejected</Badge>;
      case ProposalStatus.DENIED:
          return <Badge variant="destructive">Denied by Admin</Badge>;
      case ProposalStatus.NEGOTIATION:
        return <Badge variant="info">In Negotiation</Badge>;
      case ProposalStatus.FULFILLED:
        return <Badge variant="primary">Fulfilled</Badge>;
      case ProposalStatus.FUNDED:
        return <Badge variant="primary">Funded & In-Progress</Badge>;
      case ProposalStatus.EXECUTED:
        return <Badge variant="default">Executed & Complete</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

interface ProposalRealDataTableProps {
    onNegotiate: (proposal: CollabProposal) => void;
}

const ProposalRealDataTable: React.FC<ProposalRealDataTableProps> = ({ onNegotiate }) => {
  const { currentUser, token } = useAuth();
  const { notify } = useToast();
  const { addLog } = useLogger();
  const [data, setData] = useState<CollabProposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/proposals`, { 
        headers: {
            'Authorization': `Bearer ${token}`,
        },
        credentials: 'include' 
    });
      if (!response.ok) throw new Error('Live API failed');
      const apiData: CollabProposal[] = await response.json();
      setData(apiData.sort((a, b) => new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime()));
    } catch (e) {
        notify('Live data failed. Displaying static fallback data.', 'warn');
        addLog(`Live API failed: ${(e as Error).message}. Using imported mock data.`, LogLevel.WARN, LogCategory.API);
        setData(mockProposalData as CollabProposal[]);
    } finally {
      setIsLoading(false);
    }
  }, [notify, addLog, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProposalAction = async (proposalId: string, action: string, body = {}) => {
    if (!token) {
        notify('Authentication token is missing. Please log in again.', 'error');
        return;
    }
    try {
        const response = await fetch(`${API_BASE_URL}/api/proposals/collab/${proposalId}/${action}`.toLowerCase(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            credentials: 'include',
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to ${action} proposal`);
        }

        notify(`Proposal successfully ${action}ed!`, 'success');
        fetchData(); // Refresh data
    } catch (error) {
        console.error(`Error during ${action} for proposal ${proposalId}:`, error);
        notify((error as Error).message, 'error');
        addLog(`Failed to ${action} proposal ${proposalId}: ${(error as Error).message}`, LogLevel.ERROR, LogCategory.API);
    }
};

  const handleDelete = async (proposalId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this proposal?')) return;
    if (!token) {
        notify('Authentication token is missing. Please log in again.', 'error');
        return;
    }
    try {
        const response = await fetch(`${API_BASE_URL}/api/proposals/collab/${proposalId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to delete proposal with no further details.' }));
            throw new Error(errorData.message);
        }

        notify(`Proposal ${proposalId} deleted successfully.`, 'success');
        fetchData(); // Refresh data
    } catch (error) {
        console.error(error);
        notify((error as Error).message, 'error');
        addLog(`Failed to delete proposal ${proposalId}: ${(error as Error).message}`, LogLevel.ERROR, LogCategory.API);
    }
  };

  const handleDownload = () => {
    const isAdmin = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SYSTEM_ADMIN_LIVE;
    const dataToDownload = isAdmin ? data : data.filter((p: CollabProposal) => p.proposerId === currentUser?.id);
    
    if (dataToDownload.length === 0) {
      notify("You have no data to download.", "info");
      return;
    }

    const jsonString = JSON.stringify(dataToDownload, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = isAdmin ? 'all-collab-proposals.json' : 'my-collab-proposals.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notify("Data download started.", "success");
  };

  const renderActions = (proposal: CollabProposal) => {
    const isAdmin = currentUser?.role === UserRole.ADMIN || currentUser?.role === UserRole.SYSTEM_ADMIN_LIVE;
    const isProposer = proposal.proposerId === currentUser?.id;
    const isRecipient = proposal.artistId === currentUser?.id;

    return (
      <div className="flex items-center justify-end gap-2">
        {isAdmin && proposal.status === ProposalStatus.PENDING && (
            <>
                <Button size="sm" variant="success" onClick={() => handleProposalAction(proposal.id, 'approve')}><CheckCircle className="w-4 h-4 mr-1"/>Approve</Button>
                <Button size="sm" variant="destructive" onClick={() => handleProposalAction(proposal.id, 'deny')}><XCircle className="w-4 h-4 mr-1"/>Deny</Button>
            </>
        )}

        {isRecipient && proposal.status === ProposalStatus.ACTIVE && (
            <>
                <Button size="sm" variant="success" onClick={() => handleProposalAction(proposal.id, 'respond', { response: 'ACCEPTED' })}><CheckCircle className="w-4 h-4 mr-1"/>Accept</Button>
                <Button size="sm" variant="destructive" onClick={() => handleProposalAction(proposal.id, 'respond', { response: 'REJECTED' })}><XCircle className="w-4 h-4 mr-1"/>Reject</Button>
                <Button size="sm" variant="outline" onClick={() => onNegotiate(proposal)}><MessageSquare className="w-4 h-4 mr-1"/>Negotiate</Button>
            </> 
        )}

        {(isProposer || isRecipient) && (proposal.status === ProposalStatus.DRAFT || proposal.status === ProposalStatus.NEGOTIATION) && (
            <Button size="sm" variant="outline" onClick={() => onNegotiate(proposal)}><MessageSquare className="w-4 h-4 mr-1"/>Draft & Negotiate</Button>
        )}

        {isProposer && proposal.status === ProposalStatus.ACCEPTED && (
            <Button size="sm" variant="primary" onClick={() => handleProposalAction(proposal.id, 'fund' )}><DollarSign className="w-4 h-4 mr-1"/>Fund Escrow</Button>
        )}

        {isProposer && proposal.status === ProposalStatus.FUNDED && (
             <Button size="sm" variant="primary" onClick={() => handleProposalAction(proposal.id, 'complete' )}><CheckCircle className="w-4 h-4 mr-1"/>Mark as Fulfilled</Button>
        )}

        {isAdmin && proposal.status === ProposalStatus.FULFILLED && (
             <Button size="sm" variant="success" onClick={() => handleProposalAction(proposal.id, 'release' )}><DollarSign className="w-4 h-4 mr-1"/>Release Payment</Button>
        )}
        
        {isAdmin && proposal.status === ProposalStatus.DENIED && (
            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-400" onClick={() => handleDelete(proposal.id)}><Trash2 className="w-4 h-4 mr-1"/>Delete</Button>
        )}
      </div>
    );
  };

  const pageCount = Math.ceil(data.length / pageSize);
  const paginatedData = data.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  if (isLoading) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading All System Proposals...</p>
    </div>
  );

  if (error) return <div className="text-red-400 text-center p-8">Error fetching data: {error}</div>;

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">All System Proposals</h2>
        <Button onClick={handleDownload} className="bg-kala-secondary text-kala-900 px-4 py-2 rounded-lg font-bold hover:bg-cyan-400 transition-colors flex items-center gap-2">
          <Download className="w-4 h-4" />
          Download Data
        </Button>
      </div>
      <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
          Note: This table shows all DAO and Collaboration proposals in the system.
      </div>
      <Table>
        <TableCaption>A comprehensive list of all proposals.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Title/Event</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Proposer</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedData.map((proposal: any) => (
            <TableRow key={proposal.id}>
              <TableCell className="font-medium">{proposal.eventName || proposal.title || 'N/A'}</TableCell>
              <TableCell><Badge variant={proposal.type === 'DAO' ? 'default' : 'secondary'}>{proposal.type}</Badge></TableCell>
              <TableCell>{proposal.proposer?.name || 'N/A'}</TableCell>
              <TableCell>{getStatusBadge(proposal.status as ProposalStatus)}</TableCell>
              <TableCell>{new Date(proposal.createdAt || proposal.sentDate).toLocaleDateString()}</TableCell>
              <TableCell className="text-right">
                {proposal.type === 'COLLAB' && renderActions(proposal as CollabProposal)}
                {/* Placeholder for DAO actions if needed */}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DataTablePagination
        pageIndex={pageIndex}
        pageCount={pageCount}
        pageSize={pageSize}
        setPageIndex={setPageIndex}
        setPageSize={setPageSize}
      />
    </div>
  );
};

export default ProposalRealDataTable;
